import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight, ListChecks } from "@phosphor-icons/react";

/**
 * PortalSubmissionsGrouped — portal-only listing that groups a flat
 * list of submissions by form. Each form is an accordion header;
 * expanding reveals every submission that user made for that form,
 * with the whole row navigating to the linked ticket.
 *
 * Why not reuse SubmissionsList as-is: the portal wants a mirror of the
 * admin app which is one-form-at-a-time — this component provides that
 * per-form grouping in a single scroll, plus row-level click-through.
 *
 * Props:
 *   apiClient — portal axios instance
 *   fetchUrl  — endpoint returning the flat submissions array
 *               (defaults to /portal/submissions)
 *   ticketLinkPrefix — path prefix for row clicks (defaults to
 *                      /portal/tickets)
 */
export default function PortalSubmissionsGrouped({
  apiClient,
  fetchUrl = "/portal/submissions",
  ticketLinkPrefix = "/portal/tickets",
}) {
  const [subs, setSubs] = useState(null);
  const [openForms, setOpenForms] = useState(new Set());
  const [openAnswers, setOpenAnswers] = useState(new Set());
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient.get(fetchUrl)
      .then((r) => { if (alive) setSubs(r.data || []); })
      .catch(() => { if (alive) { setSubs([]); setError(true); } });
    return () => { alive = false; };
  }, [apiClient, fetchUrl]);

  const grouped = useMemo(() => {
    if (!subs) return [];
    const map = new Map();
    for (const s of subs) {
      const key = s.form_id || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          form_id: key,
          form_title: s.form_title || "Untitled form",
          form_issue_type: s.form_issue_type,
          items: [],
        });
      }
      map.get(key).items.push(s);
    }
    // Sort forms by most recent submission first (parity with staff feel)
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        latest: g.items.reduce((a, b) => (a.submitted_at > b.submitted_at ? a : b)).submitted_at,
      }))
      .sort((a, b) => (a.latest > b.latest ? -1 : 1));
  }, [subs]);

  const toggleForm = (id) =>
    setOpenForms((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleAnswers = (id) =>
    setOpenAnswers((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const renderVal = (v) => {
    if (v == null || v === "") return <span className="italic text-slate-400">—</span>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="italic text-slate-400">—</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  if (subs === null) return <div className="text-xs text-slate-500 italic">Loading…</div>;
  if (error) return <div className="text-xs text-red-600 italic">Couldn&apos;t load submissions.</div>;
  if (grouped.length === 0) return (
    <div className="hairline rounded-lg bg-white p-8 text-center text-sm text-slate-500" data-testid="portal-submissions-empty">
      You haven&apos;t submitted any forms yet.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="portal-submissions-grouped">
      {grouped.map((g) => {
        const isOpen = openForms.has(g.form_id);
        return (
          <div key={g.form_id} className="hairline rounded-lg bg-white overflow-hidden" data-testid={`portal-submissions-form-${g.form_id}`}>
            <button
              onClick={() => toggleForm(g.form_id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
              data-testid={`portal-submissions-form-toggle-${g.form_id}`}
            >
              <CaretRight
                size={14}
                weight="bold"
                className={`text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
              />
              <ListChecks size={16} weight="duotone" className="text-[#4f4c90] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{g.form_title}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  Last submitted {fmt(g.latest)}
                </div>
              </div>
              <span className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 !text-[11px] !py-0.5">
                {g.items.length} {g.items.length === 1 ? "submission" : "submissions"}
              </span>
            </button>

            {isOpen && (
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {g.items.map((s) => {
                  const showAnswers = openAnswers.has(s.id);
                  const canClickThrough = s.ticket_id && s.ticket_number;
                  const RowWrap = canClickThrough ? Link : "div";
                  const wrapProps = canClickThrough
                    ? {
                        to: `${ticketLinkPrefix}/${s.ticket_id}`,
                        "data-testid": `portal-submission-row-${s.id}`,
                        className: "flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer",
                      }
                    : {
                        "data-testid": `portal-submission-row-${s.id}`,
                        className: "flex items-center gap-3 px-4 py-3",
                      };
                  return (
                    <div key={s.id}>
                      <RowWrap {...wrapProps}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-800 truncate">
                            {s.ticket_subject || s.form_title || "Submission"}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono truncate">
                            Submitted {fmt(s.submitted_at)}
                          </div>
                        </div>
                        {s.ticket_number ? (
                          <span
                            className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 !text-[11px] !py-0.5"
                            title={s.ticket_subject || "Open ticket"}
                            data-testid={`portal-submission-ticket-${s.id}`}
                          >
                            #{s.ticket_number}
                          </span>
                        ) : (
                          <span className="chip !text-slate-500 !border-slate-300 bg-slate-100 !text-[11px] !py-0.5">
                            no ticket
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAnswers(s.id); }}
                          className="text-[11px] text-slate-500 hover:text-[#4f4c90] underline underline-offset-2 ml-1"
                          data-testid={`portal-submission-toggle-answers-${s.id}`}
                        >
                          {showAnswers ? "Hide answers" : "View answers"}
                        </button>
                      </RowWrap>
                      {showAnswers && (
                        <div className="mx-4 mb-3 rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1">
                          {(s.raw_answers || []).length === 0 && (
                            <div className="text-[11px] text-slate-500 italic">No custom answers on this submission.</div>
                          )}
                          {(s.raw_answers || []).map((a, i) => (
                            <div key={`${s.id}-${i}`} className="text-[12px] flex gap-2">
                              <span className="text-slate-500 font-mono min-w-[140px] shrink-0">{a.label}:</span>
                              <span className="text-slate-800 break-all">{renderVal(a.value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
